import { supabase } from '../supabase';
import { MemberWithEmail, WorkspaceInvitation } from '@/types';

interface WorkspaceMembersResponse {
  members: MemberWithEmail[];
  invitations: WorkspaceInvitation[];
}

/**
 * Extract a human-readable error message from a Supabase functions.invoke error.
 * For non-2xx responses, the error is a FunctionsHttpError whose .message
 * contains the response body as a string (JSON).
 */
function extractErrorMessage(error: any, fallback: string): string {
  if (!error) return fallback;
  // FunctionsHttpError stores parsed JSON response body in .context
  if (error.context?.error) return error.context.error;
  const msg = error.message || '';
  try {
    const parsed = JSON.parse(msg);
    if (parsed.error) return parsed.error;
  } catch {
    // not JSON
  }
  return msg || fallback;
}

/**
 * Get workspace members (with emails) and pending invitations.
 * Uses edge function because auth.users is not accessible from client.
 */
export const getWorkspaceMembers = async (
  workspaceId: string
): Promise<WorkspaceMembersResponse> => {
  const { data, error } = await supabase.functions.invoke('workspace-members', {
    body: { action: 'list', workspace_id: workspaceId },
  });
  if (error) throw new Error(extractErrorMessage(error, 'Failed to fetch members'));
  if (data?.error) throw new Error(data.error);
  return data;
};

/**
 * Invite a user to the workspace by email.
 * If the user has an account, they are added immediately.
 * If not, a pending invitation is created.
 */
export const inviteWorkspaceMember = async (
  workspaceId: string,
  email: string,
  role: 'admin' | 'reader' = 'reader'
): Promise<{ status: 'added' | 'invited' }> => {
  const { data, error } = await supabase.functions.invoke('workspace-members', {
    body: { action: 'invite', workspace_id: workspaceId, email, role },
  });
  if (error) throw new Error(extractErrorMessage(error, 'Failed to invite member'));
  if (data?.error) throw new Error(data.error);
  return data;
};

/**
 * Remove a member from the workspace.
 */
export const removeWorkspaceMember = async (
  workspaceId: string,
  userId: string
): Promise<void> => {
  const { data, error } = await supabase.functions.invoke('workspace-members', {
    body: { action: 'remove', workspace_id: workspaceId, user_id: userId },
  });
  if (error) throw new Error(extractErrorMessage(error, 'Failed to remove member'));
  if (data?.error) throw new Error(data.error);
};

/**
 * Revoke a pending invitation.
 */
export const revokeInvitation = async (
  workspaceId: string,
  invitationId: string
): Promise<void> => {
  const { data, error } = await supabase.functions.invoke('workspace-members', {
    body: { action: 'revoke-invite', workspace_id: workspaceId, invitation_id: invitationId },
  });
  if (error) throw new Error(extractErrorMessage(error, 'Failed to revoke invitation'));
  if (data?.error) throw new Error(data.error);
};

/**
 * Update a workspace member's role.
 */
export const updateMemberRole = async (
  workspaceId: string,
  userId: string,
  role: 'admin' | 'reader'
): Promise<{ status: 'updated' | 'unchanged' }> => {
  const { data, error } = await supabase.functions.invoke('workspace-members', {
    body: {
      action: 'update-role',
      workspace_id: workspaceId,
      user_id: userId,
      role,
    },
  });
  if (error) throw new Error(extractErrorMessage(error, 'Failed to update role'));
  if (data?.error) throw new Error(data.error);
  return data;
};

/**
 * Resend (refresh) a pending or revoked invitation.
 * Bumps invited_at + expires_at and forces status back to 'pending'.
 * Optionally updates the role.
 */
export const resendInvitation = async (
  workspaceId: string,
  invitationId: string,
  role?: 'admin' | 'reader'
): Promise<void> => {
  const { data, error } = await supabase.functions.invoke('workspace-members', {
    body: {
      action: 'resend-invite',
      workspace_id: workspaceId,
      invitation_id: invitationId,
      ...(role ? { role } : {}),
    },
  });
  if (error) throw new Error(extractErrorMessage(error, 'Failed to resend invitation'));
  if (data?.error) throw new Error(data.error);
};

/**
 * Accept all pending invitations for the current user.
 * Call this after login/signup to auto-join invited workspaces.
 */
export const acceptPendingInvites = async (): Promise<number> => {
  const { data, error } = await supabase.functions.invoke('accept-pending-invites');
  if (error) {
    console.warn('Failed to accept pending invites:', error.message);
    return 0;
  }
  return data?.accepted || 0;
};
